/**
 * USE STORAGE USAGE HOOK
 * 
 * React hook that provides storage usage information throughout the app.
 * Automatically loads and caches user's storage tier and usage.
 * 
 * Priority order for determining tier:
 * 1. Active subscription (from Stripe)
 * 2. Beta tester status (unlimited during beta)
 * 3. Free tier (default)
 * 
 * Usage:
 * const { 
 *   storageInfo, 
 *   tier, 
 *   percentUsed, 
 *   isLoading, 
 *   canUpload,
 *   refresh 
 * } = useStorageUsage();
 */

import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { 
  getUserStorageInfo, 
  checkCanUpload,
  syncStorageUsage,
  type UserStorageInfo,
  type UploadCheckResult 
} from '../services/storageService';
import { 
  getTier, 
  calculateStoragePercentage,
  isApproachingLimit,
  isOverStorageLimit,
  formatBytes,
  formatStorageLimit,
  getNextTier,
  type StorageTier,
  type StorageTierId
} from '../constants/storageTiers';

// Subscription info from Firestore
interface SubscriptionInfo {
  status: string;
  tier: string;
  interval: string | null;
  priceId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd?: boolean;
  // Grace period fields
  gracePeriodEnds?: Date | string | null;
  previousTier?: string | null;
  previousStorage?: number | null;
  cancelledAt?: Date | string | null;
  paymentFailedAt?: Date | string | null;
}

export interface UseStorageUsageResult {
  // Storage info
  storageInfo: UserStorageInfo | null;
  tier: StorageTier;
  tierId: StorageTierId;
  
  // Calculated values
  usedBytes: number;
  limitBytes: number;
  percentUsed: number;
  usedFormatted: string;
  limitFormatted: string;
  
  // Status flags
  isLoading: boolean;
  isApproachingLimit: boolean;
  isOverLimit: boolean;
  isBetaTester: boolean;
  
  // Subscription info
  hasActiveSubscription: boolean;
  subscription: SubscriptionInfo | null;
  
  // Grace period info
  isGracePeriod: boolean;
  isExpiredSubscription: boolean;
  gracePeriodDaysRemaining: number | null;
  
  // Actions
  canUpload: (fileSizeBytes: number) => Promise<UploadCheckResult>;
  refresh: () => Promise<void>;
  recalculate: () => Promise<void>;
  
  // Upgrade info
  nextTier: StorageTier | null;
}

// Map subscription tier names (from Stripe) to StorageTierId
// Stripe saves: casual, enthusiast, lover, pro
// StorageTierId: free, casual, enthusiast, pro, museum
function mapSubscriptionTierToTierId(subscriptionTier: string): StorageTierId {
  const tierMap: Record<string, StorageTierId> = {
    'free': 'free',
    'casual': 'casual',
    'enthusiast': 'enthusiast',
    'lover': 'pro',      // Stripe 'lover' = StorageTier 'pro' (Lacquer Lover, 5GB)
    'pro': 'museum',     // Stripe 'pro' = StorageTier 'museum' (Polish Pro, 50GB)
  };
  return tierMap[subscriptionTier] || 'free';
}

export function useStorageUsage(): UseStorageUsageResult {
  const [storageInfo, setStorageInfo] = useState<UserStorageInfo | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  
  // Load storage info and subscription when auth changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        setIsLoading(true);
        try {
          // Load storage info
          const info = await getUserStorageInfo(user.uid);
          setStorageInfo(info);
          
          // Load subscription info directly from user doc
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.subscription) {
              setSubscription(userData.subscription as SubscriptionInfo);
            } else {
              setSubscription(null);
            }
          }
        } catch (error) {
          console.error('Error loading storage info:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setUserId(null);
        setStorageInfo(null);
        setSubscription(null);
        setIsLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  // Refresh storage info (quick - from cached value)
  const refresh = useCallback(async () => {
    if (!userId) return;
    
    try {
      const info = await getUserStorageInfo(userId);
      setStorageInfo(info);
      
      // Also refresh subscription
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.subscription) {
          setSubscription(userData.subscription as SubscriptionInfo);
        }
      }
    } catch (error) {
      console.error('Error refreshing storage info:', error);
    }
  }, [userId]);
  
  // Recalculate storage (slow - actually counts files)
  const recalculate = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    try {
      await syncStorageUsage(userId);
      const info = await getUserStorageInfo(userId);
      setStorageInfo(info);
    } catch (error) {
      console.error('Error recalculating storage:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);
  
  // Check if a specific upload is allowed
  // Block uploads during grace period and expired subscription
  const canUpload = useCallback(async (fileSizeBytes: number): Promise<UploadCheckResult> => {
    if (!userId) {
      const defaultTier = getTier('free');
      return {
        allowed: false,
        reason: 'Please log in to upload photos',
        currentUsageBytes: 0,
        limitBytes: defaultTier.storageLimitBytes,
        percentUsed: 0,
        tier: defaultTier,
      };
    }
    
    // Block uploads during grace period - user can view but not add
    if (subscription?.status === 'grace_period') {
      const currentTier = getTier(mapSubscriptionTierToTierId(subscription?.tier || 'free'));
      return {
        allowed: false,
        reason: 'Your subscription has ended. Reactivate to upload new photos.',
        currentUsageBytes: storageInfo?.storageUsedBytes || 0,
        limitBytes: currentTier.storageLimitBytes,
        percentUsed: calculateStoragePercentage(storageInfo?.storageUsedBytes || 0, currentTier),
        tier: currentTier,
      };
    }
    
    return checkCanUpload(userId, fileSizeBytes);
  }, [userId, subscription?.status, subscription?.tier, storageInfo?.storageUsedBytes]);
  
  // Determine if user has an active subscription
  const hasActiveSubscription = subscription?.status === 'active';
  const isGracePeriod = subscription?.status === 'grace_period';
  const isExpiredSubscription = subscription?.status === 'expired';
  
  // Calculate grace period days remaining
  let gracePeriodDaysRemaining: number | null = null;
  if (isGracePeriod && subscription?.gracePeriodEnds) {
    const endsDate = subscription.gracePeriodEnds instanceof Date 
      ? subscription.gracePeriodEnds 
      : new Date(subscription.gracePeriodEnds);
    const msRemaining = endsDate.getTime() - Date.now();
    gracePeriodDaysRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
  }
  
  // Calculate derived values with subscription priority
  // Priority: Active subscription > Grace period (keep paid tier) > Beta tester > Free tier
  let effectiveTierId: StorageTierId;
  let effectiveIsBetaTester: boolean;
  
  if (hasActiveSubscription && subscription?.tier) {
    // User has active subscription - use subscription tier
    effectiveTierId = mapSubscriptionTierToTierId(subscription.tier);
    effectiveIsBetaTester = false;
  } else if (isGracePeriod && subscription?.tier) {
    // User is in grace period - keep their paid tier (read-only, can't add photos)
    effectiveTierId = mapSubscriptionTierToTierId(subscription.tier);
    effectiveIsBetaTester = false;
  } else if (storageInfo?.isBetaTester) {
    // User is beta tester without subscription
    effectiveTierId = storageInfo?.tierId || 'free';
    effectiveIsBetaTester = true;
  } else {
    // Regular user or expired subscription - free tier
    effectiveTierId = storageInfo?.tierId || 'free';
    effectiveIsBetaTester = false;
  }
  
  const tier = getTier(effectiveTierId);
  const usedBytes = storageInfo?.storageUsedBytes || 0;
  const limitBytes = tier.storageLimitBytes;
  const percentUsed = calculateStoragePercentage(usedBytes, tier);
  
  return {
    storageInfo,
    tier,
    tierId: effectiveTierId,
    usedBytes,
    limitBytes,
    percentUsed,
    usedFormatted: formatBytes(usedBytes),
    limitFormatted: formatStorageLimit(tier),
    isLoading,
    isApproachingLimit: !effectiveIsBetaTester && isApproachingLimit(usedBytes, tier),
    isOverLimit: !effectiveIsBetaTester && isOverStorageLimit(usedBytes, tier),
    isBetaTester: effectiveIsBetaTester,
    hasActiveSubscription,
    subscription,
    isGracePeriod,
    isExpiredSubscription,
    gracePeriodDaysRemaining,
    canUpload,
    refresh,
    recalculate,
    nextTier: getNextTier(effectiveTierId),
  };
}

export default useStorageUsage;
